import { Assert } from '@cotera/utilities';
import { RunKey, RunKeyInfo, RunKeyHelpers } from './run-keys';
import { err, ok, Result } from 'neverthrow';

export const derive: RunKeyHelpers['derive'] = (
  keyInfo: RunKeyInfo
): RunKey => {
  switch (keyInfo.t) {
    case 'workflow-program-repl': {
      const { eraQLHash } = keyInfo;
      return `workflow-program-repl|${eraQLHash}` as RunKey;
    }

    case 'workflow-program-row': {
      const { relIdentity, colIdentity, stableId } = keyInfo;
      return `workflow-program-row|${relIdentity}|${colIdentity}|${stableId}` as RunKey;
    }

    case 'conversation': {
      const { conversationId } = keyInfo;
      return `conversation|${conversationId}` as RunKey;
    }

    default:
      return Assert.unreachable(keyInfo);
  }
};

export const derivePrefixForColumn: RunKeyHelpers['derivePrefixForColumn'] = (
  relIdentity: string,
  colIdentity: string
): RunKey => {
  return `workflow-program-row|${relIdentity}|${colIdentity}` as RunKey;
};

export const decode: RunKeyHelpers['decode'] = (key: RunKey): RunKeyInfo => {
  const [head, ...rest] = key.split('|');

  const taskKind = head as RunKeyInfo['t'];

  switch (taskKind) {
    case 'workflow-program-repl': {
      Assert.assert(rest.length === 1);
      const eraQLHash = Assert.assertExists(rest[0]);
      return {
        t: 'workflow-program-repl',
        eraQLHash,
      };
    }

    case 'workflow-program-row': {
      Assert.assert(rest.length === 3);

      const relIdentity = Assert.assertExists(rest[0]);
      const colIdentity = Assert.assertExists(rest[1]);
      const stableId = Assert.assertExists(rest[2]);

      return {
        t: 'workflow-program-row',
        relIdentity,
        colIdentity,
        stableId,
      };
    }

    case 'conversation': {
      Assert.assert(rest.length === 1);
      const conversationId = Assert.assertExists(rest[0]);
      return {
        t: 'conversation',
        conversationId,
      };
    }

    default:
      return Assert.unreachable(taskKind);
  }
};

export const validate = (
  input: string
): Result<RunKey, { t: string; message?: string }> => {
  try {
    const info = decode(input as RunKey);
    const roundTripKey = derive(info);

    if (input !== roundTripKey) {
      return err({ t: 'run-key-validation-error' });
    }

    return ok(roundTripKey);
  } catch (e) {
    return err({ t: 'run-key-validation-error', message: JSON.stringify(e) });
  }
};

export const forConversation: RunKeyHelpers['forConversation'] = (
  conversationId: string
): RunKey => {
  return `conversation|${conversationId}` as RunKey;
};
